import axios from 'axios';
import {
  ChatRequest,
  ChatResponse,
  OperationResponse,
  OperationStatus,
  SessionListResponse,
  SessionMessagesResponse,
  ChatSession
} from '@/types/chat.types';
import { setupAuthInterceptor } from './authInterceptor';

const getApiBaseUrl = (): string => {
  // Get API URL from window.ENV (loaded from config.js)
  const apiUrl = window.ENV?.VITE_API_BASE_URL;

  if (!apiUrl) {
    console.error('⚠️ window.ENV.VITE_API_BASE_URL is not set! Check that config.js is loaded properly.');
    console.error('Current window.ENV:', window.ENV);
    // Use an obviously broken URL so it's immediately clear the config failed to load
    return 'http://config.not.loaded.xxx';
  }

  return apiUrl;
};

const apiClient = axios.create({
  baseURL: getApiBaseUrl(),
  headers: {
    'Content-Type': 'application/json',
  },
});

// Setup automatic token refresh interceptor
setupAuthInterceptor(apiClient);

/**
 * Get authentication headers for API requests
 * Supports both JWT tokens (authenticated users) and anonymous session tokens (guests)
 */
const getAuthHeaders = (): Record<string, string> => {
  const token = localStorage.getItem('accessToken');
  const anonymousToken = localStorage.getItem('anonymousSessionToken');

  if (token) {
    return { 'Authorization': `Bearer ${token}` };
  } else if (anonymousToken) {
    return { 'x-anonymous-session': anonymousToken };
  }

  return {};
};

/**
 * Poll an operation until it completes or fails
 */
const pollOperation = async (operationId: string): Promise<any> => {
  const headers = getAuthHeaders();
  // Azure Function timeout is 10 minutes, match polling timeout to allow full operation completion
  const maxAttempts = 600; // 600 attempts * 1 second = 600 seconds (10 minutes) max wait
  let attempts = 0;

  while (attempts < maxAttempts) {
    const response = await apiClient.get<OperationStatus>(
      `/api/chat/operation/${operationId}`,
      { headers }
    );

    if (!response.data.success) {
      throw new Error('Failed to get operation status');
    }

    const operation = response.data.operation;

    if (operation.status === 'completed') {
      return operation.result;
    }

    if (operation.status === 'failed') {
      throw new Error(operation.errorMessage || 'Operation failed');
    }

    // Wait 1 second before polling again
    await new Promise(resolve => setTimeout(resolve, 1000));
    attempts++;
  }

  throw new Error('Operation timed out');
};

export const chatService = {
  sendMessage: async (message: string, sessionId?: string): Promise<ChatResponse> => {
    const headers = getAuthHeaders();

    // Include sessionId in request body if provided
    const requestBody: ChatRequest = sessionId
      ? { message, sessionId }
      : { message };

    // Start the chat operation
    const response = await apiClient.post<OperationResponse>(
      '/api/chat/message',
      requestBody,
      { headers }
    );

    if (!response.data.success || !response.data.operationId) {
      throw new Error('Failed to start chat operation');
    }

    // Poll for the operation result
    const result = await pollOperation(response.data.operationId);

    // Convert the result to ChatResponse format
    return {
      reply: result.reply || result.message || 'No response',
      messageId: result.messageId,
      timestamp: result.timestamp || new Date().toISOString(),
      sessionId: result.sessionId
    };
  },

  /**
   * Get list of recent chat sessions
   */
  getSessions: async (limit: number = 10): Promise<ChatSession[]> => {
    const headers = getAuthHeaders();

    const response = await apiClient.get<SessionListResponse>(
      `/api/chat/sessions?limit=${limit}`,
      { headers }
    );

    if (!response.data.success) {
      throw new Error('Failed to load sessions');
    }

    // Convert timestamp strings to Date objects
    return response.data.sessions.map(session => ({
      ...session,
      lastMessageAt: new Date(session.lastMessageAt)
    }));
  },

  /**
   * Get messages for a specific session
   */
  getSessionMessages: async (sessionId: string): Promise<SessionMessagesResponse> => {
    const headers = getAuthHeaders();

    const response = await apiClient.get<SessionMessagesResponse>(
      `/api/chat/sessions/${sessionId}/messages`,
      { headers }
    );

    if (!response.data.success) {
      throw new Error('Failed to load session messages');
    }

    return response.data;
  },

  /**
   * Create a new chat session
   */
  createSession: async (): Promise<string> => {
    const headers = getAuthHeaders();

    const response = await apiClient.post<{ success: boolean; sessionId: string }>(
      '/api/chat/sessions',
      {},
      { headers }
    );

    if (!response.data.success || !response.data.sessionId) {
      throw new Error('Failed to create session');
    }

    return response.data.sessionId;
  },

  /**
   * Delete a chat session
   */
  deleteSession: async (sessionId: string): Promise<void> => {
    const headers = getAuthHeaders();

    await apiClient.delete(
      `/api/chat/sessions/${sessionId}`,
      { headers }
    );
  },

  /**
   * Rename a chat session
   */
  renameSession: async (sessionId: string, newTitle: string): Promise<void> => {
    const headers = getAuthHeaders();

    await apiClient.put(
      `/api/chat/sessions/${sessionId}/rename`,
      { title: newTitle },
      { headers }
    );
  },
};
